import React, {useCallback, useEffect} from 'react';
import styled from 'styled-components';
import {
    PreviewPosition,
    Preview3DComponentProps,
} from 'components/customer/Preview3D/types';
import PreviewControl from 'components/customer/Preview3D/components/PreviewControl';
import Preview3DNote from 'components/customer/Preview3D/components/Preview3DNote';
import useThree from 'components/customer/Preview3D/lib/useThree';
import {Loader} from 'shared/helpers';
import {Beta} from 'shared/components/Beta';
import useUI from 'shared/useUI';
import {SidebarStatus} from 'store/customer/UISlice';
import Preview3DErrorBoundary from 'components/customer/Preview3D/components/Preview3DErrorBoundary';
import {SurveyButton} from 'shared/components/Surveys/SurveyButton';
import {SURVEYS} from 'shared/components/Surveys/helpers/useSurveys';
import {ProductDataStore} from 'components/customer/Preview3D/usePreview3DData';
import {use3DContext} from 'components/customer/Preview3D/components/Preview3DComponent';
import {
    ErrorHandler,
    useValidationBanner,
} from 'components/customer/Preview3D/components/ErrorHandler';

export const Preview3dComponentWithProvider = ({
    previewLocation,
    expanded,
    fullScreen,
    hiddenForScreenshot,
    raw,
    isStandalonePreview,
}: Preview3DComponentProps<ProductDataStore>) => {
    const {values, isMiniBrowser} = use3DContext();
    const {
        setupCameraPosition,
        buttonLabel,
        previewPosition,
        setPreviewPosition,
        showTexture,
        isDoorOpenRef,
        variables,
        hasValidationErrors,
        isLoading,
        handlePreviewRef,
        updateMaterials,
        handleClick,
        setShowTexture,
    } = useThree(
        previewLocation,
        expanded,
        hiddenForScreenshot,
        fullScreen,
        raw
    );

    const {rightSidebar, isCartOverlayLayout} = useUI();

    const {debouncedHasValidationErrors, validationBannerState} =
        useValidationBanner(Boolean(hasValidationErrors));

    const previewPositionChangedCallback = useCallback(
        (position: PreviewPosition) => {
            setupCameraPosition(position);
        },
        []
    );

    const onTogglePreviewHandler = useCallback(() => {
        setShowTexture((prev) => !prev);
    }, []);

    useEffect(() => {
        updateMaterials(showTexture);
    }, [showTexture]);

    const hasDoor = typeof values.cabinet_door_gap !== 'undefined';
    const includeDrawerFaces =
        typeof values.include_drawer_faces !== 'undefined'
            ? Boolean(values.include_drawer_faces)
            : true;

    const preview3D = (
        <Preview3D
            id="preview_3d"
            $reducedViewPort={
                rightSidebar === SidebarStatus.OPEN && isCartOverlayLayout
            }
            /* eslint-disable-next-line react/jsx-no-bind --
             * The container element of other product visualiser
             * will not load if we use useCallback.
             **/
            ref={(ref) => handlePreviewRef(ref)}
            $hidden={hiddenForScreenshot}
        />
    );

    return (
        <Preview3DErrorBoundary fullScreen={fullScreen}>
            <Preview3DContainer
                $isMiniBrowser={isMiniBrowser}
                $expanded={expanded}
                $fullScreen={fullScreen}
                $hidden={hiddenForScreenshot}
                $hasError={Boolean(debouncedHasValidationErrors)}
                $noBorder={raw}>
                {raw ? (
                    preview3D
                ) : (
                    <>
                        <Loader relative loader={isLoading} />
                        <BetaStyled className="beta" />
                        <ErrorHandler
                            validationBannerState={validationBannerState}
                            isMiniBrowser={Boolean(isMiniBrowser)}
                        />
                        {!isStandalonePreview && (
                            <SurveyButton
                                surveyKey={SURVEYS.ProductVisualizer3d}
                            />
                        )}
                        {preview3D}
                        <PreviewControl
                            isStandalonePreview={isStandalonePreview}
                            handleClick={handleClick}
                            previewPosition={previewPosition}
                            setPreviewPosition={setPreviewPosition}
                            onPreviewPositionChanged={
                                previewPositionChangedCallback
                            }
                            toggleLabel={buttonLabel}
                            showToggle={
                                (Boolean(values.drawers) ||
                                    Boolean(values.shelves) ||
                                    Boolean(values.upper_shelves) ||
                                    Boolean(values.cabinet_door)) &&
                                !variables.noInteractions &&
                                !(
                                    !Boolean(variables.innerDrawerCount) &&
                                    !Boolean(values.drawers) &&
                                    hasDoor &&
                                    !includeDrawerFaces
                                )
                            }
                            onTogglePreview={onTogglePreviewHandler}
                            showTexture={showTexture}
                            isDoorOpen={isDoorOpenRef.current}
                            previewLocation={previewLocation}
                            expanded={expanded}
                            hideExpandIcon={fullScreen}
                            setShowTexture={setShowTexture}
                            updateMaterials={updateMaterials}
                        />
                    </>
                )}
            </Preview3DContainer>
            {fullScreen || hiddenForScreenshot ? null : <Preview3DNote />}
        </Preview3DErrorBoundary>
    );
};

const Preview3DContainer = styled.div<{
    $expanded: boolean;
    $fullScreen: boolean;
    $hidden: boolean;
    $noBorder: boolean;
    $isMiniBrowser: boolean;
    $hasError: boolean;
}>`
    position: relative;
    height: ${({$isMiniBrowser}) => ($isMiniBrowser ? '265px' : '375px')};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    border: 2px solid rgb(var(--primary_colour));
    ${({$noBorder, $hasError}) =>
        $noBorder && !$hasError ? `border-color: transparent;` : ''}
    ${({$hasError}) => ($hasError ? `border-color: #d32f2f;` : '')}
    overflow: hidden;
    ${({$expanded}) => ($expanded ? `height: 100%;` : '')}
    ${({$fullScreen}) =>
        $fullScreen
            ? `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 9999;
        `
            : ''}
    ${({$hidden}) =>
        $hidden ? `opacity: 0; position: fixed; pointer-events: none;` : ''}
`;

const Preview3D = styled.div<{$reducedViewPort?: boolean; $hidden: boolean}>`
    height: 100%;
    flex-grow: 1;
    background: #fff;
    border-radius: 8px 0 0 8px;
    ${({$reducedViewPort}) => ($reducedViewPort ? `width: 320px;` : '')}
    ${({$hidden}) =>
        $hidden
            ? `height: 400px;
                width: 400px;`
            : ''}
`;

const BetaStyled = styled(Beta)`
    transform: rotate(-45deg);
    left: -45px;
    top: 15px;
    width: 150px;
    font-weight: 500;
    font-size: 15px;
    pointer-events: none;
`;
